/**
 * Session Expired Modal
 * Displays when user's session has expired (401 Unauthorized)
 * Prompts user to reauthenticate
 */

import React, { useState } from 'react';
import { LogOut } from 'lucide-react';
import { Button } from '../UI/Button';
import { useAuthContext } from './AuthProvider';

interface SessionExpiredModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

export function SessionExpiredModal({ isOpen }: SessionExpiredModalProps) {
  const { logout } = useAuthContext();
  const [isClosing, setIsClosing] = useState(false);

  if (!isOpen) return null;

  const handleReauthenticate = async () => {
    setIsClosing(true);
    try {
      await logout();
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.href = '/login?session-expired=true';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-sm w-full">
        <div className="p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Session Expired
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            You've been signed out due to inactivity. Please sign in again to continue.
          </p>
          <Button
            variant="primary"
            onClick={handleReauthenticate}
            disabled={isClosing}
            className="w-full flex items-center justify-center gap-2"
          >
            {isClosing ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Signing in…
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4" />
                Sign In Again
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
